import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { getElementActualHeight } from '../../../../_metronic/assets/ts/_utils';
import { formatCompactNumber, toAbsoluteUrl } from '../../../../_metronic/helpers';
import { api } from '../../../modules/api/api';
import KeywordTypeInitialsGroup from '../../../modules/components/badge/KeywordTypeInitialsGroup';
import ListLoading from '../../../modules/components/loading/ListLoading';
import ImageModal from '../../../modules/components/modal/ImageModal';
import {
    getSocialMediaFromLink,
    messengerSocialMedias,
    SocialMedia,
} from '../../../modules/components/socialMedia/mappings';
import SocialMediaIconGroup from '../../../modules/components/socialMedia/SocialMediaIconGroup';
import { useToast } from '../../../modules/components/toast/ToastContext';
import {
    DataSiftField,
    KeywordType,
    SiRequestType,
    SiService,
    WebCrawlStatus,
} from '../mappings';
import BrandDetail from '../si/detail/sections/brand/BrandDetectionDetail';
import CategoryDetail from '../si/detail/sections/category/CategoryDetail';
import CustomerDisputeDetail from '../si/detail/sections/CustomerDisputeDetail';
import DomainDetail from '../si/detail/sections/domain/DomainContentDetail';
import GeoInfoDetail from '../si/detail/sections/domain/GeoInfoDetail';
import SecurityDetail from '../si/detail/sections/domain/SecurityDetail';
import WhoisDetail from '../si/detail/sections/domain/WhoisDetail';
import InboundDetail from '../si/detail/sections/InboundFlowDetail';
import OutboundDetail from '../si/detail/sections/OutboundFlowDetail';
import Remarks from '../si/detail/sections/Remarks';
import SocialMediaDetail from '../si/detail/sections/SocialMediaFlowDetail';
import Summary from '../si/detail/sections/Summary';
import WebContentDetail from '../si/detail/sections/WebContentDetail';
import WebPolicyDetail from '../si/detail/sections/WebPolicyDetail';
import { getActiveItemsOnly } from '../si/detail/utils';
import RiskAssessmentDetail from '../si/detail/sections/RiskAssessmentDetail';
import AuditRemarksDetail from '../si/detail/sections/AuditRemarksDetail';
import ComplianceAdviceDetail from '../si/detail/sections/ComplianceAdviceDetail';
import TrafficSearchDetail from '../si/detail/sections/TrafficSearchDetail';
import MccDetail from '../si/detail/sections/MccDetail';
import { setLanguage, useLang } from '../../../../_metronic/i18n/Metronici18n';
import './SiReportPrint.scss';

export default function Detail() {
    const intl = useIntl();
    const { setToast } = useToast();
    const selectedLang = useLang();
    const params = useParams();
    const requestId = params.id;
    const paramLanguage = params?.language?.toLowerCase();
    const normalizedParamLanguage =
        paramLanguage === 'en' || paramLanguage === 'sc' || paramLanguage === 'tc'
            ? paramLanguage
            : 'en';

    useEffect(() => {
        if (selectedLang !== normalizedParamLanguage) {
            setLanguage(normalizedParamLanguage);
        }
    }, [normalizedParamLanguage, selectedLang]);

    const currentLanguage = intl.locale as 'sc' | 'en' | 'tc';
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string>(null);
    const [screenshots, setScreenshots] = useState(undefined);

    // TODO: change to state & add resize observer to update on resize
    const headerElement = document.getElementById('kt_header');
    const toolbarElement = document.getElementById('kt_toolbar');
    const topbarHeight =
        (headerElement ? getElementActualHeight(headerElement) : 0) +
        (toolbarElement ? getElementActualHeight(toolbarElement) : 0);

    const { data, status } = useQuery(
        ['public-si-report-detail', requestId],
        async () => {
            try {
                const endpoint = new URL(
                    `public/v1/si_report_detail/${requestId}`,
                    process.env.REACT_APP_SITE_INTELLIGENCE_BACKEND_SERVICE_BASE_URL
                ).href;

                const { result, status } = await fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        'X-Service-Client': 'cbp',
                    },
                }).then(async (resp) => {
                    const result = await resp?.json();
                    return { result: result, status: result?.code ?? resp?.status };
                });
                const resolvedResult = await result;
                if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
                    return resolvedResult;
                }
            } catch (err) {
                const response = await err?.response?.json();
                setToast({
                    show: true,
                    type: 'Danger',
                    header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
                    message: response?.message ?? err.message,
                });
            }
        },
        { keepPreviousData: true }
    );

    const isSiAiNewData = data?.results?.queries?.find((query) => query?.service === SiService.SI_AI_SERVICE) ? true
        : false;
    const requestType = data?.results?.request_type as SiRequestType;

    const isRemarksCardDirty = false;
    const isCarry = data?.results?.carry_info?.is_carried ?? false;
    const carryRequestId = data?.results?.carry_info?.source_request_id;

    const results = data?.results?.sites;
    const domainResults = results?.find((item) => item.service === SiService.DOMAIN_SIFT_SERVICE);
    let webResults: any;
    const sexualResults = results?.find((item) => item.service === SiService.ADULT_SHIELD_SERVICE);
    const tobaccoResults = results?.find((item) => item.service === SiService.TOBACCO_SIFT_SERVICE);
    const brandResults = results?.find((item) => item.service === SiService.BRAND_DETECT_SERVICE);
    const inboundResults = results?.find((item) => item.service === SiService.INBOUND_FLOW_SERVICE);
    const outboundResults = results?.find((item) => item.service === SiService.OUTBOUND_FLOW_SERVICE);
    const socialOutboundResults = results?.find(
        (item) => item.service === SiService.SOCIAL_OUTBOUND_FLOW_SERVICE
    );
    const customerDisputeResults = results?.find(
        (item) => item.service === SiService.CUSTOMER_DISPUTE_SERVICE
    );
    const relatedIdentityResults = results?.find((item) => item.service === SiService.TRACE_SERVICE);
    const membersOnlyResults = results?.find(
        (item) => item.service === SiService.MEMBERS_ONLY_SIFT_SERVICE
    );
    const categoryResults = results?.find((item) => item.service === SiService.CATEGORIZE_SERVICE);
    const siAiResults = results?.find((item) => item.service === SiService.SI_AI_SERVICE);
    const siAiTranslations = siAiResults?.result?.translations ?? [];
    const remarkOptionResults = useMemo(() => ({ ...data }), [data]);

    let relatedWebResults = [];

    // separate user request and related identity request
    // TODO: Update after add created_by in site
    const relatedIdentityWebContentRequestQueueIdSet = new Set();
    if (relatedIdentityResults?.detail?.length > 0) {
        for (const detail of relatedIdentityResults.detail) {
            // Skip round 0
            if (detail?.round === 0) continue;

            for (const relatedData of detail?.related_data ?? []) {
                const queueId = relatedData?.web_content_queue?.queue_id;
                if (queueId) {
                    relatedIdentityWebContentRequestQueueIdSet.add(queueId);
                }
            }
        }
        webResults = results
            ?.toSorted((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            ?.find(
                (item) =>
                    item.service === SiService.DATA_SIFT_SERVICE &&
                    !relatedIdentityWebContentRequestQueueIdSet.has(item.queue_id)
            );
        relatedWebResults = results?.filter(
            (item) =>
                item.service === SiService.DATA_SIFT_SERVICE &&
                relatedIdentityWebContentRequestQueueIdSet.has(item.queue_id)
        );
    } else {
        webResults = results?.find((item) => item.service === SiService.DATA_SIFT_SERVICE);
    }

    const messengerResults: Set<string> = new Set();
    if (socialOutboundResults && socialOutboundResults.result?.fqdn_group?.count > 0) {
        for (const item of socialOutboundResults.detail) {
            const socialMedia = getSocialMediaFromLink(item.found_link);
            if (socialMedia && messengerSocialMedias.includes(socialMedia)) {
                messengerResults.add(item.found_link);
            }
        }
    }

    if (messengerResults.size > 0) {
        webResults = {
            ...webResults,
            result: {
                ...webResults?.result,
                messenger: Array.from(messengerResults),
            },
        };
    }

    const { data: captureData, status: captureStatus } = useQuery(
        ['site-intelligence-capture', params, data],
        async () => {
            try {
                const endpoint = new URL(
                    `public/v1/list_capture`,
                    process.env.REACT_APP_SITE_INTELLIGENCE_BACKEND_SERVICE_BASE_URL
                ).href;

                const { result, status } = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'X-Service-Client': 'cbp',
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify({
                        filters: {
                            request_id: isCarry ? [carryRequestId, params.id] : params.id,
                            status: ["ACTIVE"],
                        },
                        sort_order: {
                            created_at: -1,
                        },
                        offset: 0,
                        limit: 0,
                    }),
                }).then(async (resp) => {
                    const result = await resp?.json();
                    return { result: result, status: result?.code ?? resp?.status };
                });
                const resolvedResult = await result;
                if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
                    return resolvedResult?.results?.data;
                }
            } catch (err) {
                const response = await err?.response?.json();
                setToast({
                    show: true,
                    type: 'Danger',
                    header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
                    message: response?.message ?? err.message,
                });
            }
        },
        { keepPreviousData: true }
    );

    useEffect(() => {
        if (captureData) {
            setScreenshots(captureData);
        }
    }, [captureData]);

    enum ServiceResultStatus {
        PENDING = 'PENDING',
        PROCESSING = 'PROCESSING',
        COMPLETED = 'COMPLETED',
        SKIPPED = 'SKIPPED',
        STOPPED = 'STOPPED',
        TOBACCO_FOUND = 'TOBACCO_FOUND',
        WEB_CRAWL_FAIL = 'WEB_CRAWL_FAIL',
        SERVICE_FAIL = 'SERVICE_FAIL',
        ERROR = 'ERROR',
    }

    enum DomainSubService {
        DOMAIN = 'DOMAIN',
        WHOIS = 'WHOIS',
        SECURITY = 'SECURITY',
        GEO_INFO = 'GEO_INFO',
    }

    function getServiceResultStatus(
        service: SiService,
        subservice?: DomainSubService,
        serviceResult?: any
    ): ServiceResultStatus {
        const data = serviceResult ?? results?.find((item) => item.service === service);
        if (!data) return undefined;
        const { status, result } = data;

        if (status === 'FAILED') {
            return ServiceResultStatus.SERVICE_FAIL;
        } else if (status !== ServiceResultStatus.COMPLETED) {
            return status;
        } else if (!subservice && (result?.status === WebCrawlStatus.FAILED || result?.error_message)) {
            return ServiceResultStatus.WEB_CRAWL_FAIL;
        }

        switch (service) {
            case SiService.DOMAIN_SIFT_SERVICE:
                switch (subservice) {
                    case DomainSubService.WHOIS:
                        return !Object.keys(result?.domain_detail ?? {}).length
                            ? ServiceResultStatus.ERROR
                            : ServiceResultStatus.COMPLETED;
                    case DomainSubService.SECURITY:
                        return result?.security_info?.ssl_info?.error &&
                            Object.keys(result?.security_info?.ssl_info ?? {}).length === 1 && // ssl_info failed
                            Object.values(result?.security_info?.safety_info ?? { dummy: false }).every(
                                (value) => value === null
                            ) // safety_info failed
                            ? ServiceResultStatus.ERROR
                            : ServiceResultStatus.COMPLETED;
                    case DomainSubService.GEO_INFO:
                        return result?.status === WebCrawlStatus.FAILED || result?.error_message
                            ? ServiceResultStatus.WEB_CRAWL_FAIL
                            : ServiceResultStatus.COMPLETED;
                    case DomainSubService.DOMAIN:
                    default:
                        return (result?.status === WebCrawlStatus.FAILED || result?.error_message) && // puppeteer failed
                            !Object.keys(result?.domain_detail ?? {}).length && // whois failed
                            !result?.domain_info?.ip // fetch ip failed
                            ? ServiceResultStatus.WEB_CRAWL_FAIL
                            : ServiceResultStatus.COMPLETED;
                }
            case SiService.CUSTOMER_DISPUTE_SERVICE:
                const sources = result?.sources;
                const isCrawlError = sources?.find((source) => source?.error_message !== null) ?? false;
                const isSkipped =
                    sources.length === 1 && sources[0]?.status === ServiceResultStatus.SKIPPED;
                return isCrawlError
                    ? ServiceResultStatus.WEB_CRAWL_FAIL
                    : isSkipped
                        ? ServiceResultStatus.SKIPPED
                        : ServiceResultStatus.COMPLETED;
            default:
                return ServiceResultStatus.COMPLETED;
        }
    }

    function getCustomerDisuputeResult(contentData: any): number {
        const sources = contentData?.result?.sources;
        if (!Array.isArray(sources) || sources.length === 0) return 0;

        return sources.reduce(
            (total, source) =>
                total +
                (source?.rating_review_count?.one_star ?? 0) +
                (source?.rating_review_count?.two_stars ?? 0) +
                (source?.rating_review_count?.three_stars ?? 0),
            0
        );
    }

    function getSecurityErrorCount(domainServiceResult?: any): number {
        const securityInfo = domainServiceResult?.result?.security_info;
        if (!securityInfo) return 0;

        let count = 0;

        if (securityInfo?.ssl_info?.error != null) {
            count += 1;
        }

        count += Object.values(securityInfo?.safety_info ?? {}).filter(
            (value) => value === true
        ).length;

        return count;
    }

    function getServiceCount(service: SiService, serviceResult?: any): number {
        const data = serviceResult ?? results?.find((item) => item.service === service);
        if (!data?.result) return 0;

        switch (service) {
            case SiService.INBOUND_FLOW_SERVICE:
            case SiService.OUTBOUND_FLOW_SERVICE:
            case SiService.SOCIAL_OUTBOUND_FLOW_SERVICE:
                return data.result.total_found_links || 0;
            case SiService.CUSTOMER_DISPUTE_SERVICE:
                return getCustomerDisuputeResult(data);
            case SiService.TRACE_SERVICE:
                return data.result?.related_data?.fqdn?.length || 0;
            default:
                return 0;
        }
    }

    const combinedBrandData: {
        status: ServiceResultStatus;
        brands: string[];
    } = {
        status: undefined,
        brands: [],
    };
    if (brandResults || categoryResults) {
        const brandResultsAvailable = brandResults?.result?.summary?.by_brand?.length > 0;
        const categoryResultsAvailable = categoryResults?.result?.brands?.length > 0;
        combinedBrandData.brands = Array.from(
            new Set([
                ...(brandResultsAvailable ? brandResults?.result?.summary?.by_brand : []),
                ...(categoryResultsAvailable
                    ? getActiveItemsOnly(categoryResults?.result?.brands, 'brand')
                    : []),
            ])
        );
        combinedBrandData.status =
            brandResultsAvailable && categoryResultsAvailable
                ? ServiceResultStatus.COMPLETED
                : getServiceResultStatus(SiService.BRAND_DETECT_SERVICE) ??
                getServiceResultStatus(SiService.CATEGORIZE_SERVICE);
    }

    const categoryStatusMap = {
        [SiService.ADULT_SHIELD_SERVICE]: getServiceResultStatus(SiService.ADULT_SHIELD_SERVICE),
        [SiService.TOBACCO_SIFT_SERVICE]: getServiceResultStatus(SiService.TOBACCO_SIFT_SERVICE),
        [SiService.MEMBERS_ONLY_SIFT_SERVICE]: getServiceResultStatus(
            SiService.MEMBERS_ONLY_SIFT_SERVICE
        ),
        [SiService.CATEGORIZE_SERVICE]: getServiceResultStatus(SiService.CATEGORIZE_SERVICE),
    };
    const combinedCategoryData: {
        status: ServiceResultStatus;
        categories: Array<KeywordType | SiService>;
    } = {
        status: undefined,
        categories: [],
    };
    if (Object.values(categoryStatusMap).some((value) => value !== undefined)) {
        const appliedCategoriesStatuses = Object.entries(categoryStatusMap).filter(
            ([_, value]) => value
        );
        if (appliedCategoriesStatuses.some(([_, value]) => value === ServiceResultStatus.PENDING)) {
            combinedCategoryData.status = ServiceResultStatus.PENDING;
        } else if (
            appliedCategoriesStatuses.some(([key, value]) => value === ServiceResultStatus.PROCESSING)
        ) {
            combinedCategoryData.status = ServiceResultStatus.PROCESSING;
        } else {
            for (const [key, status] of appliedCategoriesStatuses) {
                if (status !== ServiceResultStatus.COMPLETED) continue;
                else if (
                    (key === SiService.ADULT_SHIELD_SERVICE && sexualResults?.result?.adult) ||
                    (key === SiService.TOBACCO_SIFT_SERVICE && tobaccoResults?.result?.tobacco) ||
                    (key === SiService.MEMBERS_ONLY_SIFT_SERVICE && membersOnlyResults?.result?.members_only)
                ) {
                    combinedCategoryData.categories.push(key);
                } else if (
                    key === SiService.CATEGORIZE_SERVICE &&
                    categoryResults?.result?.categories?.length > 0
                ) {
                    combinedCategoryData.categories.push(
                        ...getActiveItemsOnly(categoryResults.result.categories, 'category')
                    );
                }
            }
            combinedCategoryData.status =
                combinedCategoryData.categories.length === 0 &&
                    appliedCategoriesStatuses.some(([_, value]) => value !== ServiceResultStatus.COMPLETED)
                    ? ServiceResultStatus.WEB_CRAWL_FAIL
                    : ServiceResultStatus.COMPLETED;
        }
    }

    const previewCategoryLimit = 2;

    const previewSocialMediaLimit = 2;
    const tocUniqueSocialMedia: { socialMedia: SocialMedia; link?: string }[] = [];
    if (socialOutboundResults && socialOutboundResults?.result?.fqdn_group?.count > 0) {
        const pastSocialMedia = new Set();
        for (const item of socialOutboundResults?.detail ?? []) {
            if (tocUniqueSocialMedia.length >= previewSocialMediaLimit) break;
            const socialMedia = getSocialMediaFromLink(item?.found_link);
            if (!pastSocialMedia.has(socialMedia)) {
                pastSocialMedia.add(socialMedia);
                tocUniqueSocialMedia.push({ socialMedia, link: item?.found_link });
            }
        }
    }

    type TableOfContentItem = {
        title: string;
        path: string;
        status: ServiceResultStatus;
        isActive?: boolean;
        errorMessage?: string;
        summaryCount?: number;
        summaryLabel?: string;
        isDanger?: boolean;
        previewTypes?: Array<KeywordType | SiService>;
        socialPreview?: Array<{ socialMedia: SocialMedia; link?: string }>;
        socialPreviewTotal?: number;
        children?: TableOfContentItem[];
    };

    const getTocServiceTitle = (service: string) =>
        intl.formatMessage({ id: `SI_KEYWORD_TYPE_BADGE.${service}` });
    const tableOfContents: TableOfContentItem[] = [
        {
            title: intl.formatMessage({ id: 'SI_SUMMARY.TITLE' }),
            path: '#',
            status: ServiceResultStatus.COMPLETED,
        },
        ...(isSiAiNewData ? [{
            title: intl.formatMessage({ id: 'SI_RISK_ASSESSMENT.TITLE' }),
            path: '#risk-assessment-content',
            status: combinedCategoryData.status,
        }] : []),
        ...(isSiAiNewData ? [{
            title: intl.formatMessage({ id: 'SI_AUDIT_REMARKS.TITLE' }),
            path: '#audit-remarks-content',
            status: combinedCategoryData.status,
        }] : []),
        ...((isSiAiNewData && categoryResults?.result?.detected_mcc) ? [{
            title: intl.formatMessage({ id: 'SI_MCC_DETAIL.TITLE' }),
            path: '#mcc-content',
            status: combinedCategoryData.status,
        }] : []),
        {
            title: intl.formatMessage({ id: 'SI_REMARKS.TITLE' }),
            path: '#remarks',
            // TODO: update from API
            status: ServiceResultStatus.COMPLETED,
        },
        {
            title: getTocServiceTitle(SiService.DOMAIN_SIFT_SERVICE),
            path: '#domain-content',
            status: getServiceResultStatus(SiService.DOMAIN_SIFT_SERVICE, DomainSubService.DOMAIN),
            errorMessage: domainResults?.result?.error_message,
        },
        {
            title: intl.formatMessage({ id: 'SI_DOMAIN.SECURITY' }),
            path: '#security-content',
            status: getServiceResultStatus(SiService.DOMAIN_SIFT_SERVICE, DomainSubService.SECURITY),
            errorMessage: domainResults?.result?.security_info?.ssl_info?.error,
            summaryCount: getSecurityErrorCount(domainResults),
            summaryLabel: 'Errors',
            isDanger: getSecurityErrorCount(domainResults) > 0,
        },
        ...(isSiAiNewData ? [{
            title: intl.formatMessage({ id: 'SI_TRAFFIC_SEARCH_DETAIL.TITLE' }),
            path: '#traffic-search-optimization-content',
            status: combinedCategoryData.status,
        }] : []),
        ...requestType === SiRequestType.SI ? [{
            title: intl.formatMessage({ id: 'SI_DOMAIN.GEO_TARGETING' }),
            path: '#geo-content',
            status: getServiceResultStatus(SiService.DOMAIN_SIFT_SERVICE, DomainSubService.GEO_INFO),
            errorMessage: domainResults?.result?.error_message,
        }] : [],
        {
            title: getTocServiceTitle(SiService.DATA_SIFT_SERVICE),
            path: '#web-content',
            status: getServiceResultStatus(SiService.DATA_SIFT_SERVICE, null, webResults),
            errorMessage: webResults?.result?.error_message,
        },
        {
            title: intl.formatMessage({ id: 'SI_WEB_POLICY_DETAIL.TITLE' }),
            path: '#web-policy',
            status: getServiceResultStatus(SiService.DATA_SIFT_SERVICE, null, webResults),
            errorMessage: webResults?.result?.error_message,
        },
        {
            title: getTocServiceTitle(SiService.BRAND_DETECT_SERVICE),
            path: '#brand-content',
            status: combinedBrandData.status,
            summaryCount: combinedBrandData.brands.length,
            summaryLabel: 'Brands',
            isDanger: combinedBrandData.brands.length > 0,
        },
        {
            title: getTocServiceTitle(SiService.INBOUND_FLOW_SERVICE),
            path: '#inbound-content',
            status: getServiceResultStatus(SiService.INBOUND_FLOW_SERVICE),
            errorMessage: inboundResults?.result?.error_message,
            summaryCount: getServiceCount(SiService.INBOUND_FLOW_SERVICE),
            summaryLabel: 'FQDNs',
            isDanger: getServiceCount(SiService.INBOUND_FLOW_SERVICE) === 0,
        },
        ...requestType === SiRequestType.SI ? [{
            title: getTocServiceTitle(SiService.OUTBOUND_FLOW_SERVICE),
            path: '#outbound-content',
            status: getServiceResultStatus(SiService.OUTBOUND_FLOW_SERVICE),
            errorMessage: outboundResults?.result?.error_message,
            summaryCount: getServiceCount(SiService.OUTBOUND_FLOW_SERVICE),
            summaryLabel: 'FQDNs',
        }] : [],
        {
            title: getTocServiceTitle(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE),
            path: '#social-outbound-content',
            status: getServiceResultStatus(SiService.SOCIAL_OUTBOUND_FLOW_SERVICE),
            errorMessage: socialOutboundResults?.result?.error_message,
            socialPreview: tocUniqueSocialMedia,
            socialPreviewTotal:
                socialOutboundResults?.detail?.length ??
                socialOutboundResults?.result?.fqdn_group?.count ??
                0,
        },
        {
            title: getTocServiceTitle(SiService.CUSTOMER_DISPUTE_SERVICE),
            path: '#customer-dispute-content',
            status: getServiceResultStatus(SiService.CUSTOMER_DISPUTE_SERVICE) ?? getServiceResultStatus(SiService.CATEGORIZE_SERVICE),
            summaryCount: getServiceResultStatus(SiService.CUSTOMER_DISPUTE_SERVICE) ? getServiceCount(SiService.CUSTOMER_DISPUTE_SERVICE) : undefined,
            summaryLabel: 'Reviews',
            errorMessage: customerDisputeResults?.result?.sources?.reduce(
                (acc, source, idx) =>
                    source?.error_message !== null
                        ? acc.concat(`${acc.length > 0 ? '; ' : ''}${source?.error_message}`)
                        : acc,
                ''
            ),
        },
        {
            title: getTocServiceTitle(SiService.TRACE_SERVICE),
            path: '#related-identity-content',
            status: getServiceResultStatus(SiService.TRACE_SERVICE),
            errorMessage: relatedIdentityResults?.result?.error_message,
            summaryCount: getServiceCount(SiService.TRACE_SERVICE),
            summaryLabel: 'FQDNs',
        },
        {
            title: getTocServiceTitle(SiService.CATEGORIZE_SERVICE),
            path: '#category-content',
            status: combinedCategoryData.status,
            isDanger: combinedCategoryData.categories.length > 0,
            previewTypes: combinedCategoryData.categories,
        },
        ...(isSiAiNewData ? [{
            title: intl.formatMessage({ id: 'SI_COMPLIANCE_ADVICE_DETAIL.TITLE' }),
            path: '#compliance-advice-content',
            status: combinedCategoryData.status,
        }] : []),
        {
            title: intl.formatMessage({ id: 'SI_DOMAIN.APPENDIX_A_WHOIS_RECORD' }),
            path: '#whois-content',
            status: getServiceResultStatus(SiService.DOMAIN_SIFT_SERVICE, DomainSubService.WHOIS),
        },
    ];

    function renderServiceResultStatus(status: ServiceResultStatus, errorMessage?: string) {
        let icon = null;
        switch (status) {
            case ServiceResultStatus.PENDING:
                icon = <i className='bi bi-hourglass text-warning fs-3 align-middle'></i>;
                break;
            case ServiceResultStatus.PROCESSING:
                icon = <i className='bi bi-hourglass-split text-primary fs-3 align-middle'></i>;
                break;
            case ServiceResultStatus.SKIPPED:
                icon = <i className='bi bi-skip-end-circle text-success fs-3 align-middle'></i>;
                break;
            case ServiceResultStatus.STOPPED:
                icon = <i className='bi bi-stop-circle text-muted fs-3 align-middle'></i>;
                break;
            case ServiceResultStatus.SERVICE_FAIL:
            case ServiceResultStatus.WEB_CRAWL_FAIL:
            case ServiceResultStatus.ERROR:
                icon = <i className='fa fa-exclamation-circle text-danger fs-3 align-middle'></i>;
                break;
        }

        if (icon) {
            return (
                <OverlayTrigger
                    placement='top'
                    overlay={
                        <Tooltip>
                            {status}
                            {errorMessage && `: ${errorMessage}`}
                        </Tooltip>
                    }
                >
                    <span>{icon}</span>
                </OverlayTrigger>
            );
        }
    }

    function renderTableOfContentSummary(item: TableOfContentItem) {
        const hasPreviewTypes = !!item.previewTypes?.length;
        const hasCount = typeof item.summaryCount === 'number';

        if (!hasCount && !hasPreviewTypes) return null;

        return (
            <span className='d-inline-flex align-items-center ms-2'>
                {hasCount && (
                    <span className={clsx('fw-normal fs-8', item.isDanger ? 'text-danger' : 'text-muted')}>
                        <div className='symbol symbol-circle symbol-30px border-light'>
                            <div
                                className={clsx(
                                    'symbol-label fs-8 fw-bold text-light',
                                    item.isDanger ? 'bg-danger' : 'bg-success'
                                )}
                            >
                                {formatCompactNumber(item.summaryCount)}
                            </div>
                        </div>
                    </span>
                )}
                {hasPreviewTypes && (
                    <span className={clsx('ms-2', !hasCount && 'd-inline-block')}>
                        <KeywordTypeInitialsGroup types={item.previewTypes} limit={previewCategoryLimit} />
                    </span>
                )}
            </span>
        );
    }

    const handleExpandImage = (imagePath: string) => {
        setSelectedImage(imagePath);
        setShowImageModal(true);
    };

    const defaultCaptureId = screenshots?.find(
        (item) => item?.vpn === domainResults?.result?.geo_info?.selected
    )?.capture_id;

    return (
        <div
            className='si-print-root'
            data-bs-spy='scroll'
            data-bs-target='#table-of-contents'
            data-bs-offset='0'
            tabIndex={0}
        >
            <ImageModal
                imagePath={selectedImage}
                show={showImageModal}
                onHide={() => setShowImageModal(false)}
                restoreFocus={false} // prevent restoring focus to previously focused element on hide
            />
            <div className='ps-2 si-print-logo'>
                <div>
                    <img
                        alt='Logo'
                        className='h-20px w-auto logo mt-6'
                        src={toAbsoluteUrl('/media/logos/logo.png')}
                    />
                </div>
                <div className='my-6 fs-2'>SiteInspect Report</div>
            </div>

            {status === 'success' && data?.results && captureStatus === 'success' && (
                <div className='d-flex flex-column mt-2 mt-xl-5'>
                    <div className='row g-5 g-md-6'>
                        {/* Left Column: Main Content */}
                        <div className='col-12 col-md-9 col-xl-10 d-flex flex-column'>
                            {/* Report Summary (Header) */}
                            <div id='summary' style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}>
                                <Summary
                                    data={data}
                                    socialOutboundResults={socialOutboundResults}
                                    domainResults={domainResults}
                                    screenshots={screenshots?.filter(
                                        (item) => item.service === SiService.DOMAIN_SIFT_SERVICE
                                    )}
                                    webResults={webResults}
                                    categoryResults={categoryResults}
                                    isEditable={false}
                                    isSiAiNewData={isSiAiNewData}
                                    handleExpandImage={handleExpandImage}
                                />
                            </div>

                            {/* Details */}
                            <>
                                {categoryResults && isSiAiNewData && (
                                    <div
                                        id='risk-assessment-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <RiskAssessmentDetail data={categoryResults} currentLanguage={currentLanguage} translation={siAiTranslations} refetch={() => { }} />
                                    </div>
                                )}

                                {categoryResults && isSiAiNewData && (
                                    <div
                                        id='audit-remarks-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <AuditRemarksDetail data={categoryResults} refetch={() => { }} />
                                    </div>
                                )}

                                {categoryResults && isSiAiNewData && categoryResults?.result?.detected_mcc && (
                                    <div
                                        id='mcc-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <MccDetail data={categoryResults} currentLanguage={currentLanguage} translation={siAiTranslations} refetch={() => { }} />
                                    </div>
                                )}

                                <div id='remarks' style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}>
                                    <Remarks
                                        data={remarkOptionResults}
                                        handleRefetch={() => { }}
                                        captures={screenshots}
                                        isSiteSaving={false}
                                        isDirty={isRemarksCardDirty}
                                        isEditable={false}
                                        isPublic={true}
                                    />
                                </div>

                                {domainResults && (
                                    <div
                                        id='domain-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <DomainDetail data={domainResults} status={status} refetch={() => { }} />
                                    </div>
                                )}
                                {domainResults && (
                                    <div
                                        id='security-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <SecurityDetail data={domainResults} status={status} refetch={() => { }} />
                                    </div>
                                )}
                                {categoryResults && inboundResults && isSiAiNewData && (
                                    <div
                                        id='traffic-search-optimization-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <TrafficSearchDetail categoryData={categoryResults} inboundData={inboundResults} currentLanguage={currentLanguage} translation={siAiTranslations} refetch={() => { }} />
                                    </div>
                                )}
                                {domainResults && requestType === SiRequestType.SI && (
                                    <div
                                        id='geo-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <GeoInfoDetail
                                            data={domainResults}
                                            status={status}
                                            refetch={() => { }}
                                            screenshotData={screenshots?.filter(
                                                (item) => item.service === SiService.DOMAIN_SIFT_SERVICE
                                            )}
                                            onExpandImage={handleExpandImage}
                                        />
                                    </div>
                                )}
                                {webResults && (
                                    <>
                                        <div
                                            id='web-content'
                                            style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                        >
                                            <WebContentDetail
                                                data={webResults}
                                                reviewer={data?.results?.users}
                                                status={status}
                                                refetch={() => { }}
                                                isEditable={false}
                                            />
                                        </div>

                                        <div
                                            id='web-policy'
                                            style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                        >
                                            <WebPolicyDetail
                                                data={webResults}
                                                isSiAiNewData={isSiAiNewData}
                                                reviewer={data?.results?.users}
                                                status={status}
                                                refetch={() => { }}
                                                isEditable={false}
                                            />
                                        </div>
                                    </>
                                )}
                                {combinedBrandData.status && (
                                    <div
                                        id='brand-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <BrandDetail
                                            data={{
                                                [SiService.BRAND_DETECT_SERVICE]: brandResults,
                                                [SiService.CATEGORIZE_SERVICE]: categoryResults,
                                            }}
                                            isSiAiNewData={isSiAiNewData}
                                            isCarry={isCarry}
                                            carryRequestId={carryRequestId}
                                            reviewer={data?.results?.users}
                                            status={status}
                                            refetch={() => { }}
                                            isVisible={!!combinedBrandData.status}
                                            isEditable={false}
                                            isPublic={true}
                                        />
                                    </div>
                                )}
                                {inboundResults && (
                                    <div
                                        id='inbound-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <InboundDetail
                                            data={inboundResults}
                                            isSiAiNewData={isSiAiNewData}
                                            reviewer={data?.results?.users}
                                            status={status}
                                            refetch={() => { }}
                                            isEditable={false}
                                        />
                                    </div>
                                )}
                                {outboundResults && requestType === SiRequestType.SI && (
                                    <div
                                        id='outbound-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <OutboundDetail
                                            data={outboundResults}
                                            reviewer={data?.results?.users}
                                            status={status}
                                            refetch={() => { }}
                                            isEditable={false}
                                        />
                                    </div>
                                )}
                                {socialOutboundResults && (
                                    <div
                                        id='social-outbound-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <SocialMediaDetail
                                            data={socialOutboundResults}
                                            isSiAiNewData={isSiAiNewData}
                                            reviewer={data?.results?.users}
                                            status={status}
                                            refetch={() => { }}
                                            isEditable={false}
                                        />
                                    </div>
                                )}
                                {(customerDisputeResults || categoryResults) && (
                                    <div
                                        id='customer-dispute-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <CustomerDisputeDetail
                                            data={customerDisputeResults}
                                            categoryData={categoryResults}
                                            status={status}
                                            currentLanguage={currentLanguage}
                                            translation={siAiTranslations}
                                            refetch={() => { }}
                                            isEditable={false}
                                        />
                                    </div>
                                )}

                                {combinedCategoryData.status && (
                                    <div
                                        id='category-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <CategoryDetail
                                            data={{
                                                [SiService.ADULT_SHIELD_SERVICE]: sexualResults,
                                                [SiService.TOBACCO_SIFT_SERVICE]: tobaccoResults,
                                                [SiService.MEMBERS_ONLY_SIFT_SERVICE]: membersOnlyResults,
                                                [SiService.CATEGORIZE_SERVICE]: categoryResults,
                                            }}
                                            screenshots={screenshots}
                                            isSiAiNewData={isSiAiNewData}
                                            reviewer={data?.results?.users}
                                            status={status}
                                            currentLanguage={currentLanguage}
                                            translation={siAiTranslations}
                                            refetch={() => { }}
                                            isEditable={false}
                                            defaultCaptureId={defaultCaptureId}
                                        />
                                    </div>
                                )}

                                {categoryResults && isSiAiNewData && (
                                    <div
                                        id='compliance-advice-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <ComplianceAdviceDetail data={categoryResults} currentLanguage={currentLanguage} translation={siAiTranslations} refetch={() => { }} />
                                    </div>
                                )}

                                {domainResults && (
                                    <div
                                        id='whois-content'
                                        style={{ scrollMarginTop: '9.75rem', scrollBehavior: 'smooth' }}
                                    >
                                        <WhoisDetail data={domainResults} status={status} refetch={() => { }} />
                                    </div>
                                )}
                            </>
                        </div>

                        {/* Right Column: Category, Table of Content */}
                        <div className='d-none d-md-block col-md-3 col-xl-2'>
                            {/* Sticky Table of Content */}
                            <div
                                className='card card-flush d-none d-md-flex sticky-top p-6 z-index-1'
                                style={{
                                    top: `${topbarHeight + 25}px`,
                                }}
                            >
                                <div className='card-body p-0'>
                                    <div
                                        className='overflow-auto'
                                        style={{
                                            maxHeight: `calc(100vh - ${topbarHeight + 25 * 2}px - 3rem - 35px - 1rem)`,
                                            paddingRight: '0.5rem',
                                        }} // p-6 = 1.5rem each side; 35px + 1rem = button height + mt-4
                                    >
                                        <ul
                                            id='table-of-contents'
                                            className='nav nav-flush menu menu-column menu-title-gray-600 menu-bullet-gray-300 menu-state-primary fw-semibold fs-6'
                                        >
                                            {tableOfContents.map((tocItem, index) => {
                                                const renderItem = (item: TableOfContentItem, depth = 0) => {
                                                    return (
                                                        item.status && (
                                                            <div key={index}>
                                                                <li
                                                                    className='menu-item py-2 d-flex align-items-center'
                                                                    key={item.path}
                                                                    style={{ marginInline: `${depth}rem`, minHeight: '45px' }}
                                                                >
                                                                    <a
                                                                        href={item.path}
                                                                        data-kt-scroll-toggle='true'
                                                                        className='menu-link p-0 nav-link align-items-center gap-4'
                                                                    >
                                                                        <div>
                                                                            <span className='bullet bullet-dot align-middle bg-secondary'></span>
                                                                        </div>
                                                                        <span className='menu-title text-break d-block'>
                                                                            <span className='d-flex align-items-center gap-2'>
                                                                                <span className='flex-grow-1'>{item.title}</span>
                                                                                {item.socialPreview && item.socialPreview.length > 0 && (
                                                                                    <span className='d-flex align-items-center gap-1 ms-2'>
                                                                                        <SocialMediaIconGroup
                                                                                            items={item.socialPreview}
                                                                                            limit={previewSocialMediaLimit}
                                                                                        />
                                                                                        {item.socialPreviewTotal &&
                                                                                            item.socialPreviewTotal > previewSocialMediaLimit && (
                                                                                                <small className='ms-1'>
                                                                                                    +
                                                                                                    {item.socialPreviewTotal -
                                                                                                        previewSocialMediaLimit}
                                                                                                </small>
                                                                                            )}
                                                                                    </span>
                                                                                )}
                                                                                {/* <span>
                                                                                    {renderServiceResultStatus(
                                                                                        item.status,
                                                                                        item?.errorMessage
                                                                                    )}
                                                                                </span> */}
                                                                                {renderTableOfContentSummary(item)}
                                                                            </span>
                                                                        </span>
                                                                    </a>
                                                                </li>
                                                                {item?.children?.map((child, index) =>
                                                                    renderItem(child, depth + 1)
                                                                )}
                                                            </div>
                                                        )
                                                    );
                                                };
                                                return renderItem(tocItem);
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='si-print-page-footer-space'>Copyright © AUSTREME All Rights Reserved.</div>
                </div>
            )}

            {status === 'loading' && (
                <div className='card mb-5 mb-xl-10 mt-2'>
                    <div className='card-body p-0'>
                        <ListLoading
                            label={intl.formatMessage({ id: 'COMMON.LOADING' })}
                            customStyles={{ top: '50%' }}
                        />
                    </div>
                </div>
            )}

            {status !== 'loading' && !data?.results && (
                <div className='card mb-5 mb-xl-10 mt-2 mt-xl-5'>
                    <div className='card-body p-6'>Report not ready</div>
                </div>
            )}
        </div>
    );
}
